// import * as fundebug from 'fundebug-javascript'
// @ts-expect-error - Nuxt will resolve this path at runtime
// import { init } from 'fundebug-javascript'
import {
  getOrCreateSessionId,
  getOrCreateUserId,
  getCurrentTraceId,
  encodeTrackingToUserName,
  getUidFromCookie
} from '~/utils/tracking'

// 使用 XMLHttpRequest 发送日志（备选方案）
function sendWithXHR(url: string, data: any) {
  try {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.setRequestHeader('DEVICE', 'pc')
    xhr.timeout = 5000 // 5秒超时

    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (process.env.NODE_ENV === 'development') {
          console.info('[Fundebug] XMLHttpRequest 日志收集成功')
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Fundebug] XMLHttpRequest 响应异常:', xhr.status, xhr.statusText)
        }
      }
    }

    xhr.onerror = function () {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Fundebug] XMLHttpRequest 请求失败')
      }
    }

    xhr.ontimeout = function () {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Fundebug] XMLHttpRequest 请求超时')
      }
    }

    xhr.send(JSON.stringify(data))
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Fundebug] XMLHttpRequest 异常:', error)
    }
  }
}

// 扩展 / 用户脚本专有的 API 或标识（应用自身代码不会引用，已确认全仓库 0 匹配）
const EXTENSION_MESSAGE_PATTERN =
  /\buserScripts\b|\bGM_[A-Za-z]+\b|\bunsafeWindow\b|Greasemonkey|Tampermonkey|Violentmonkey|\bbrowser\.runtime\b|\bchrome\.runtime\b/i
// 浏览器扩展的资源协议
const EXTENSION_PROTOCOL_PATTERN =
  /chrome-extension:\/\/|moz-extension:\/\/|safari-extension:\/\/|safari-web-extension:\/\//i
// 匿名注入代码来源（Firefox 对 eval / new Function / 扩展注入脚本上报的 fileName）
const ANONYMOUS_SOURCE_PATTERN = /<anonymous code>|<anonymous>/i

/**
 * 判断一条 Fundebug 错误事件是否来自浏览器扩展 / 用户脚本（油猴）注入的代码。
 *
 * 这类错误并非应用自身代码产生，而是用户安装的浏览器扩展（如 Greasemonkey、Tampermonkey、
 * Violentmonkey 等用户脚本管理器，或其它 WebExtension）向页面上下文注入脚本后抛出的异常，
 * 会被 Fundebug 的全局 window.onerror 捕获，进而污染线上监控与后端错误日志。
 *
 * 典型案例（本次线上日志）：
 *   Firefox 下扩展注入的脚本引用 WebExtensions 的 `browser.userScripts` / `chrome.userScripts`
 *   API，在页面主世界中 `userScripts` 未定义，抛出：
 *     ReferenceError: userScripts is not defined
 *   其 fileName 为 "<anonymous code>"、stacktrace 为 "@<anonymous code>:1:1"，
 *   指向匿名注入代码而非应用打包产物（应用代码 fileName 必为真实文件，如 /_nuxt/xxx.js）。
 */
function isBrowserExtensionNoise(event: any): boolean {
  try {
    const message = String(event?.message || '')
    const fileName = String(event?.fileName || '')
    const stacktrace = String(event?.stacktrace || event?.stack || '')
    const type = String(event?.type || '')

    // 1) 错误信息引用了扩展 / 用户脚本专有的 API 或标识
    if (EXTENSION_MESSAGE_PATTERN.test(message)) {
      return true
    }

    // 2) 错误来源指向浏览器扩展的资源协议
    if (EXTENSION_PROTOCOL_PATTERN.test(fileName) || EXTENSION_PROTOCOL_PATTERN.test(stacktrace)) {
      return true
    }

    // 3) 未捕获的全局错误，来源为匿名注入代码，且堆栈中不含任何应用真实产物
    if (
      type === 'uncaught' &&
      ANONYMOUS_SOURCE_PATTERN.test(fileName) &&
      !/_nuxt\/|\.js:|jufair\.com/.test(stacktrace)
    ) {
      return true
    }

    return false
  } catch {
    // 过滤逻辑自身异常时，保守按“非噪音”处理，避免漏报真实错误
    return false
  }
}

export default defineNuxtPlugin(async app => {
  let fundebug:any = {
    init: ()=> {}
  }
  try { 
    // @ts-ignore
   fundebug = await import('fundebug-javascript')
  } catch (error) {
    console.warn('[Fundebug] 错误:', error)
  }
  // const fundebug = await import('fundebug-javascript')
  
  // @ts-ignore
  // import('fundebug-revideo') // fundebug-revideo 0.7.1已更新至0.8.0

  // 初始化追踪信息
  const sessionId = getOrCreateSessionId()
  const userId = getOrCreateUserId()
  const traceId = getCurrentTraceId()
  const uid = getUidFromCookie()

  // 编码追踪信息到 user.name
  const encodedUserName = encodeTrackingToUserName()

  console.log('📊 Fundebug 追踪信息初始化 (PC):', {
    sessionId,
    userId,
    traceId,
    uid,
    encodedUserName
  })

  // 临时邮箱
  // uolnja59421@chacuo.net
  // 获取运行时配置
  const config = useRuntimeConfig()
  const baseUrl = config.public.config?.API_BASE_URL || '/api'

  fundebug.init({
    apikey: '2f5acb3be02f3ebc9a576021a47a42e195d43a15877191373ed4a25d1667a236',
    releasestage: process.env.NODE_ENV,
    silentResource: true,
    setHttpBody: true,
    httpTimeout: 3000, // 监控超过3000毫秒的http请求
    silentDev: true, // 不收集开发环境中的错误

    // 设置用户标识（用于追踪）
    // name 字段编码格式：sessionId|traceId|uid
    user: {
      name: encodedUserName,
      id: userId
    },

    // 添加元数据字段（会在每个错误报告中包含）
    metaData: {
      sessionId: sessionId,
      traceId: traceId,
      userId: userId,
      platform: 'pc',
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
    },

    // 在错误发送前添加额外信息
    beforeNotify: function (event: any) {
      // 更新最新的追踪信息到 user.name（编码格式：sessionId|traceId|uid）
      const latestEncodedName = encodeTrackingToUserName()
      event.user = event.user || {}
      event.user.name = latestEncodedName
      event.user.id = getOrCreateUserId()

      // 同时在 metaData 中保存原始值（便于查看）
      event.metaData = event.metaData || {}
      event.metaData.sessionId = getOrCreateSessionId()
      event.metaData.traceId = getCurrentTraceId()
      event.metaData.userId = getOrCreateUserId()
      event.metaData.uid = getUidFromCookie()
      event.metaData.timestamp = new Date().toISOString()
      event.metaData.encodedUserName = latestEncodedName

      if (typeof window !== 'undefined') {
        event.metaData.url = window.location.href
      }
      // 注意: clientIp 由 Fundebug 自动获取

      return event
    },

    // 使用 callback 接收上报到 Fundebug 服务器的错误数据，并双写到后端
    callback: function (event: any) {
      // 过滤浏览器扩展 / 用户脚本（油猴）注入的错误，避免污染后端错误日志。
      // 注意：Fundebug 的 filters 只拦截“上报到 Fundebug 服务器”的请求，而 callback 会先于 filters
      // 无条件触发，因此必须在 callback 内单独过滤，才能阻止这类噪音写入后端 collect 日志
      // （即阿里云 SLS 采集的 /software/docker/jufair/logs/fundebug/error.log 的来源）。
      if (isBrowserExtensionNoise(event)) {
        return
      }

      // 异步发送到后端，不等待响应，避免阻塞 Fundebug 上报
      if (!baseUrl) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Fundebug] baseUrl 未配置，跳过日志收集')
        }
        return
      }

      // 构建收集 URL：保证路径含 /api（勿用 includes('/api')，会误匹配主机名 api.jufair.com）
      let collectUrl = String(baseUrl).replace(/\/+$/, '')
      const hasApiPath = /\/api$/i.test(collectUrl) || /\/api\//i.test(collectUrl)
      if (!hasApiPath) {
        collectUrl += '/api'
      }
      collectUrl += '/fundebug/collect'

      if (process.env.NODE_ENV === 'development') {
        console.info('[Fundebug] 发送日志到:', collectUrl)
      }

      // 创建 event 副本并删除 sequence 字段（内容太多）
      const eventToSend = { ...event }
      if (eventToSend.redo) {
        delete eventToSend.redo
      }

      // 直接使用 XMLHttpRequest 发送日志（已验证可用）
      sendWithXHR(collectUrl, eventToSend)
    },

    filters: [
      // ===== 浏览器扩展 / 用户脚本（油猴）注入的噪音错误 =====
      // 来自用户浏览器扩展而非应用代码，例如：ReferenceError: userScripts is not defined
      {
        message: EXTENSION_MESSAGE_PATTERN
      },
      {
        fileName: EXTENSION_PROTOCOL_PATTERN
      },
      {
        stacktrace: EXTENSION_PROTOCOL_PATTERN
      },
      {
        // 未捕获错误且来源为匿名注入代码（扩展脚本典型特征，应用产物 fileName 必为真实文件）
        type: /^uncaught$/,
        fileName: ANONYMOUS_SOURCE_PATTERN
      },
      {
        target: {
          src: /sfp.safe.baidu.com/
        }
      },
      {
        target: {
          src: /sofire.baidu.com/
        }
      },
      {
        target: {
          src: /miao.baidu.com/
        }
      },
      {
        message: /Page not found-数据不存在/
      }, // 没有数据(如:exhibition-0-0-1-11-0-10-2/ 最后一个参数是第二页)
      {
        message: /Page not found-已下架/
      }, // 数据已下架
      {
        message: /验证码不正确/
      },
      {
        target: {
          src: /www.madkapital.com/
        }
      },
      {
        target: {
          src: /vue.jufair.com/
        }
      },
      {
        name: /ErrorCaptured/,
        message: /nextTick/
      }, // https://miao.baidu.com/abdr?_o=错误
      {
        name: /ErrorCaptured/,
        message: /mounted hook/
      }, // 过滤表单校验提示
      {
        res: {
          status: /^403$/
        }
      }, // 过滤403错误
      {
        res: {
          status: /^404$/
        }
      } // 过滤404错误
    ]
  } as any)

  if (process.client) {
    window.fundebug = fundebug
  }

  // 提供给 Nuxt 应用使用
  app.provide('fundebug', fundebug)

  console.log('✅ Fundebug 初始化完成 (PC)')

  // app.config.globalProperties.$fundebug = fundebug
  // app.use(fundebugVue)
})
